/**
 * SimplifiedSignup flow identities (`FlowConfig.name`, exposed via
 * `useSimplifiedSignupContext()`) allowed to render the Stripe Checkout Sessions form
 * when enrolled in `www-checkout-sessions`.
 *
 * This is defense-in-depth alongside the `useCheckoutSessions` conjunction in `index.tsx`.
 * That conjunction describes what a surface must *resolve to* to be eligible; this describes
 * which surfaces have actually been SSR-plumbed and QA'd for the CS form. If Amplitude
 * targeting is ever widened to devices on a surface not in this list — including a
 * brand-new SimplifiedSignup page nobody has reviewed against Checkout Sessions yet —
 * the code gate and the targeting can silently disagree, since it may still happen to
 * satisfy that conjunction. Keeping an explicit allowlist means a
 * targeting change alone can never light up CS on an unverified surface; landing on a
 * new surface requires a code change here too.
 */
export const CHECKOUT_SESSIONS_ELIGIBLE_FLOW_NAMES: ReadonlySet<string> = new Set([
	// /signup-flow (Control), both the default PreSignupFlow branch and the
	// post-upsell-goals branch — both pass `isCheckoutSessionsEnabled` directly.
	'SimplifiedSignup',
	'AnnualOffer', // /new-annual-offer
	'MonthlyOffer', // /monthly-offer
	'QuarterlyOffer', // /quarterly-offer
	'LifetimeOffer', // /new-lifetime-offer
	'BlogDiscountOffer', // /blog-discount-offer
	'spotify', // /spotify, /link-with-spotify
	'MobileSubscribe', // /ios-subscribe, /ios-subscribe-new
	// In-app upsell modal (AppLayout: ControlModal default, locked-content variant,
	// web-app-redesign variant). The modal is client-only — it opens on user action, after
	// `appLoaded` — so unlike the SSR-plumbed offer pages it resolves enrollment from
	// AppLayout's own client `useAmplitudeExperiment(CHECKOUT_SESSIONS)` read (resolved well
	// before any modal opens, since AppLayout mounts globally at app load) and threads it as
	// `isCheckoutSessionsEnabled` into the provider it mounts. See AppLayout/index.tsx.
	'Simplified Signup Modal',
	'LockedContentUpsell',
	'WebAppUpsell',
	// /family/signup — the family-plan upsell payment surface (familyPlan/PaymentForm), which
	// mounts a minimal single-step SimplifiedSignupContext (see its own flowConfig.ts) purely to
	// reuse this component's Checkout-Sessions-vs-legacy decision point.
	'FamilyPlanUpsellPayment',
]);
